<template>
  <div ref="container" class="tw-relative tw-z-50">
    <!-- Button and badge container -->
    <div class="tw-relative tw-flex tw-items-center tw-justify-center">
      <button @click="isActive = !isActive"  class=" tw-relative tw-w-[40px] tw-h-[40px] tw-text-primary-950 hover:tw-text-primary-600 tw-duration-200">
          <icon icon="solar:bell-linear" class="tw-text-2xl" />
          <span v-if="unreadCount > 0" class="tw-text-xs tw-text-white tw-font-medium tw-absolute -tw-top-0 tw-right-3 tw-bg-primary-500 tw-rounded-full tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center">
            {{ unreadCount > 99 ? '99+' : unreadCount }}
          </span>
      </button>

    </div>

    <transition
      class="tw-duration-200"
      enter-from-class="tw-translate-y-[10px] tw-opacity-0"
      leave-to-class="tw-translate-y-[10px] tw-opacity-0"
    >
      <div v-if="isActive">
        <div
          @click.stop
          :class="[$root.$i18n.locale == 'ar' ? 'tw-left-0' : '-tw-right-24']"
          class="md:tw-w-[320px] md:tw-max-h-[450px] tw-w-[300px] tw-max-h-[300px] tw-mx-auto tw-absolute -tw-bottom-1 tw-translate-y-full tw-bg-white tw-rounded-xl tw-shadow-lg tw-overflow-hidden "
        >
          <!-- Simple Header -->
          <div class="tw-p-3 tw-border-b tw-border-gray-100">
            <div class="tw-flex tw-items-center tw-justify-between">
              <h3 class="tw-font-medium tw-text-gray-800">{{ t('common.notifications', 'Notifications') }}</h3>
              <button 
                v-if="notifications.length > 0" 
                @click="markAllAsRead" 
                class="tw-text-xs tw-text-primary-600 hover:tw-text-primary-700">
                {{ t('buttons.clear-all', 'Clear all') }}
              </button>
            </div>
          </div>
          
          <!-- Tabs -->
          <div class="tw-flex tw-border-b tw-border-gray-100">
            <button 
              @click="activeTab = 'all'"
              class="tw-flex-1 tw-py-2 tw-text-sm tw-font-medium"
              :class="[
                activeTab === 'all' 
                  ? 'tw-text-primary-600 tw-border-b-2 tw-border-primary-500' 
                  : 'tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors'
              ]">
              {{ t('common.all', 'All') }}
            </button>
            <button 
              @click="activeTab = 'unread'"
              class="tw-flex-1 tw-py-2 tw-text-sm tw-font-medium"
              :class="[
                activeTab === 'unread' 
                  ? 'tw-text-primary-600 tw-border-b-2 tw-border-primary-500' 
                  : 'tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors'
              ]">
              {{ t('common.unread', 'Unread') }}
            </button>
          </div>
          
          <!-- Notifications list -->
          <div class="tw-overflow-y-auto" style="max-height: 350px;">
            <div v-if="loading" class="tw-flex tw-justify-center tw-items-center tw-py-4">
              <div class="tw-animate-spin tw-h-5 tw-w-5 tw-border-2 tw-border-primary-500 tw-rounded-full tw-border-t-transparent"></div>
            </div>
            
            <template v-else-if="filteredNotifications.length">
              <SingleNotification 
                v-for="notification in filteredNotifications" 
                :key="notification.id"
                :notification="notification"
                @notification-read="markAsRead"
                @popup-state-changed="handlePopupStateChange"
              />
            </template>
            
            <!-- Empty state -->
            <div v-else class="tw-py-8 tw-px-4 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
              <div class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-mb-3">
                <icon icon="solar:bell-broken" class="tw-text-gray-400 tw-text-xl" />
              </div>
              <p class="tw-text-gray-700 tw-font-medium">{{ t('common.no-notifications', 'No notifications') }}</p>
              <p class="tw-text-xs tw-text-gray-500 tw-mt-1">{{ t('common.check-later', 'Check back later for updates') }}</p>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
  
<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { useAuthStore } from '@/stores';
import useAlert from '@/composables/useAlert';
//import useNotification from '@/services/notification';
import SingleNotification from '@/components/notifications/SingleNotification.vue';

const { t } = useI18n();
const authStore = useAuthStore();
const alert = useAlert;
// Initialize notification service at the top level
//const notificationService = useNotification();

const isActive = ref(false);
const container = ref(null);
const loading = ref(true);
const activeTab = ref('all');
const notifications = ref([]);
const unreadCount = ref(0);
const notificationManager = ref(null);
const isAnyPopupOpen = ref(false);

// Track popup state changes
function handlePopupStateChange(isOpen) {
  isAnyPopupOpen.value = isOpen;
}

// Close dropdown when clicking outside, but only if no popup is open
onClickOutside(container, () => {
  if (!isAnyPopupOpen.value) {
    isActive.value = false;
  }
});

// Get only the notifications matching the active tab
const filteredNotifications = computed(() => {
  if (activeTab.value === 'unread') {
    return notifications.value.filter(notification => notification.unread);
  }
  return notifications.value;
});

// Initialize the notification system
onMounted(async () => {
  if (authStore.user && authStore.user.id) {
    await initNotificationManager();
  }
});

// Clean up listeners when component is unmounted
onUnmounted(() => {
  if (notificationManager.value) {
    // Remove event listeners
    notificationManager.value.removeAllEventListeners();
  }
});

// Initialize the notification manager
async function initNotificationManager() {
  try {
    console.log('Initializing notification manager...');
    // Import the notification manager dynamically to avoid SSR issues
    const { notificationManager: manager } = await import('@/services/notificationManager');
    notificationManager.value = manager;
    
    // Initialize with current user ID
    await notificationManager.value.init(authStore.user.id);
    
    // Add event listeners
    notificationManager.value.addEventListener('onNotificationReceived', handleNewNotification);
    notificationManager.value.addEventListener('onNotificationUpdated', handleNotificationUpdate);
    notificationManager.value.addEventListener('onNotificationDeleted', handleNotificationDeletion);
    notificationManager.value.addEventListener('onCountUpdated', handleCountUpdate);
    
    // Set initial notifications from the manager
    notifications.value = notificationManager.value.notifications;
    console.log('Notifications loaded:', notifications.value);
    
    // Set unread count and finish loading
    unreadCount.value = notificationManager.value.unreadCount;
    loading.value = false;
  } catch (error) {
    console.error('Failed to initialize notification manager:', error);
    loading.value = false;
    // Just show empty state instead of demo data
    notifications.value = [];
    unreadCount.value = 0;
  }
}

// Handle new notification
function handleNewNotification(notification) {
  // Check if notification already exists to prevent duplicates
  const existingIndex = notifications.value.findIndex(n => n.id === notification.id);
  if (existingIndex !== -1) {
    console.log(`Notification with ID ${notification.id} already exists in component, updating instead of adding new`);
    notifications.value[existingIndex] = notification;
    return;
  }
  
  // Add to the beginning of the array
  notifications.value = [notification, ...notifications.value];
  
  // Show a toast for new notifications
  if (notification.type) {
    // Check for type to display correct alert variant
    const alertType = notification.type === 'danger' ? 'danger' : 
                     notification.type === 'error' ? 'danger' : notification.type;
    alert(notification.message, alertType);
  }
}

// Handle notification update
function handleNotificationUpdate(updatedNotification) {
  const index = notifications.value.findIndex(n => n.id === updatedNotification.id);
  if (index !== -1) {
    notifications.value[index] = updatedNotification;
  }
}

// Handle notification deletion
function handleNotificationDeletion(deletedNotification) {
  notifications.value = notifications.value.filter(n => n.id !== deletedNotification.id);
}

// Handle unread count update
function handleCountUpdate(count) {
  unreadCount.value = count;
}

// Mark all notifications as read
function markAllAsRead() {
  if (notificationManager.value) {
    notificationManager.value.markAllAsRead();
  } else {
    // Just reset in case manager is not available
    notifications.value = notifications.value.map(notification => ({
      ...notification,
      unread: false
    }));
    unreadCount.value = 0;
  }
}

// Mark a single notification as read
function markAsRead(notificationId) {
  console.log(`UserNotification (app): markAsRead called for notification ${notificationId}`);
  if (notificationManager.value) {
    notificationManager.value.markAsRead(notificationId);
  } else {
    console.log('NotificationManager not available, updating locally');
    // Update local copy if manager not available
    const index = notifications.value.findIndex(n => n.id === notificationId);
    if (index !== -1) {
      notifications.value[index].unread = false;
      unreadCount.value = Math.max(0, unreadCount.value - 1);
    }
  }
}
</script>
  
<style>
</style>